import React, { useState, useEffect } from "react";
import {
  FaTimes,
  FaBuilding,
  FaCheck,
  FaPhone,
  FaEnvelope,
  FaUser,
  FaMapMarkerAlt,
  FaCheckCircle,
  FaCalendarAlt,
  FaStickyNote,
  FaMugHot,
  FaArrowLeft,
  FaArrowRight,
} from "react-icons/fa";
import { MdAddBusiness } from "react-icons/md";
import { Alert, Snackbar } from "@mui/material";
import { CheckCircle, Error } from "@mui/icons-material";
import {
  buildFirmCreatePayload,
  validateFirmCreateStep,
  firstInvalidFirmCreateStep,
  FIRM_CREATE_STEPS,
} from "../../utils/firmCreateForm";
import useAuth from "../../Hooks/useAuth";
import { useAddFirmMutation, useGetCreateFirmProductOptionsQuery } from "../../store";
import Loader from "../Utils/Loader";
import { Stepper } from "../shared";
import FirmInfoStep from "./createFirm/FirmInfoStep";
import FirmContactStep from "./createFirm/FirmContactStep";
import FirmProductsStep from "./createFirm/FirmProductsStep";
import FirmSettingsReviewStep from "./createFirm/FirmSettingsReviewStep";
import useDrawerTransition from "../../Hooks/useDrawerTransition";
import { getFirmTypeStyle } from "../../utils/firmTypeStyles";
import { getFirmIcon } from "../../utils/firmUtils";

const CreateFirmDrawer = ({
  open,
  onClose,
  onFirmCreated,
  defaultMainFirmName,
  defaultFirmType,
}) => {
  // Kapalı pozisyonda mount → kayarak açılır; kapanışta çıkış animasyonu
  // bitene kadar monte kalır (form sıfırlaması da o ana ertelenir).
  const { mounted, entered } = useDrawerTransition(open);
  const { auth } = useAuth();
  const token = auth.accessToken;
  const [add, resultAdd] = useAddFirmMutation();

  // Form state'leri (firmType: customer | dealer | storage — yalnızca biri).
  // activeProducts null = ürün listesi henüz gelmedi; gelince hepsi seçilir (#275)
  const initialInput = () => ({
    firmName: "",
    firmType: defaultFirmType || "customer",
    orderFrequencyDays: 30,
    isActive: true,
    mainFirmName: defaultMainFirmName || "",
    // Üst firma bayi ise (#373) — { id, firmName }; ana firmayla birlikte olmaz
    dealerFirm: null,
    isCorporate: false,
    ownerName: "",
    // Muhataplar (#367)
    contacts: [],
    officialNumber: "",
    taxNumber: "",
    taxOffice: "",
    email: "",
    tel: "",
    note: "",
    address: { text: "", city: "", town: "" },
    activeProducts: null,
    token: token,
  });
  const [input, setInput] = useState(initialInput);

  // Ürün adımı (#275) — drawer açıkken çekilir
  const productOptions = useGetCreateFirmProductOptionsQuery(token, { skip: !mounted || !token });
  const products = React.useMemo(
    () => productOptions.data?.data?.products || [],
    [productOptions.data],
  );
  // Liste alınamadıysa ürün adımı engellenmez; alan gönderilmez → sunucu hepsini açar
  const productsAvailable = !productOptions.isError;

  // UI state'leri
  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    message: "",
  });
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdFirm, setCreatedFirm] = useState(null);

  // Form validation
  const [errors, setErrors] = useState({});

  // Sıfırlama kapanış kayması BİTİNCE (mounted false) yapılır — `open`e
  // bağlıyken form drawer hâlâ ekrandayken boşalıyordu.
  useEffect(() => {
    if (!mounted) {
      setInput(initialInput());
      setAlert({ open: false, type: "success", message: "" });
      setStep(1);
      setIsSubmitting(false);
      setCreatedFirm(null);
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted]);

  // Varsayılan: tüm ürünler seçili gelir (kullanıcı kararı 2026-10-15)
  useEffect(() => {
    if (mounted && products.length && input.activeProducts === null) {
      setInput((prev) => ({ ...prev, activeProducts: [...products] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, products, input.activeProducts]);

  // Firma tipi değişince seçili ana firmayı sıfırla
  useEffect(() => {
    if (input.firmType !== "storage") {
      setInput((prev) => ({ ...prev, mainFirmName: "none", dealerFirm: null }));
    }
  }, [input.firmType]);

  useEffect(() => {
    if (alert.open && alert.type === "error") {
      setTimeout(() => {
        setAlert({ open: false, type: "success", message: "" });
      }, 3000);
    }
  }, [alert]);

  useEffect(() => {
    if (resultAdd.isError === true) {
      // Server'ın anlamlı hata mesajını göster (ör. "Geçerli bir telefon
      // numarası sağlayın"); yoksa genel mesaja düş.
      const serverMsg = resultAdd.error?.data?.message;
      setAlert({
        open: true,
        type: "error",
        message: serverMsg || "İşlem başarısız tekrar deneyin !",
      });
      setIsSubmitting(false);
    }
    if (resultAdd.isSuccess === true && !createdFirm) {
      if (resultAdd.data) {
        onFirmCreated(resultAdd.data);
        // Başarı ekranı GÖNDERİLEN veriyi gösterir (varsayılan tel/e-posta dahil)
        setCreatedFirm({ ...buildFirmCreatePayload(input), dealerFirm: input.dealerFirm, ...resultAdd.data?.data });
      }
      setIsSubmitting(false);
    }
  }, [resultAdd.isSuccess, resultAdd.isError]);

  const handleCloseAlert = () => {
    setAlert({ open: false, type: "success", message: "" });
  };

  // "İleri" yalnız bulunulan adımı doğrular (#275)
  const handleNext = () => {
    const stepErrors = validateFirmCreateStep(step, input, { productsAvailable });
    setErrors(stepErrors);
    if (Object.keys(stepErrors).length === 0) setStep((s) => s + 1);
  };

  const handleBack = () => {
    setErrors({});
    setStep((s) => Math.max(1, s - 1));
  };

  // Boş e-posta/tel varsayılanları inputa YAZILMAZ — özet gönderilecek veriyi
  // (buildFirmCreatePayload) gösterir; "Geri" kutuları bozmaz (#261)
  const handleConfirmSubmit = () => {
    const opts = { productsAvailable };
    const invalidStep = firstInvalidFirmCreateStep(input, opts);
    if (invalidStep) {
      setErrors(validateFirmCreateStep(invalidStep, input, opts));
      setStep(invalidStep);
      return;
    }
    setIsSubmitting(true);
    add(buildFirmCreatePayload(productsAvailable ? input : { ...input, activeProducts: null }));
  };

  const handleInputChange = (field, value) => {
    setInput((prev) => {
      const next = { ...prev, [field]: value };
      // Depo seçilince ana firma otomatik Bayıner
      if (field === "firmType") {
        if (value === "storage") {
          next.mainFirmName = "Bayıner";
        } else {
          next.mainFirmName = "none";
        }
        next.dealerFirm = null;
      }
      return next;
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleAddressChange = (field, value) => {
    setInput((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value,
      },
    }));
  };

  if (!mounted) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-[9998] transition-opacity duration-300 ease-in-out ${entered ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed inset-y-0 right-0 w-full max-w-2xl bg-white shadow-2xl z-[9999] transform ${entered ? "translate-x-0" : "translate-x-full"} transition-transform duration-300 ease-in-out`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex-none bg-gradient-to-r from-blue-600 to-blue-700 p-4 sm:p-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MdAddBusiness className="w-6 h-6" />
                <h2 className="text-lg sm:text-xl font-bold">
                  Yeni Firma Oluştur
                </h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-white/20 transition-colors"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            <p className="text-blue-100 mt-2 text-sm">
              Yeni firma bilgilerini girerek sisteme ekleyin
            </p>
          </div>

          {/* Adımlar (#275) — oluşturma/başarı ekranında gizli */}
          {!createdFirm && !isSubmitting && (
            <div className="flex-none px-6 pt-5 pb-3 border-b border-gray-100">
              <Stepper steps={FIRM_CREATE_STEPS} current={step} />
            </div>
          )}
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {createdFirm ? (
              <div className="space-y-5">
                {/* Başarı banner */}
                <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <FaCheckCircle className="w-8 h-8 text-emerald-500 shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-emerald-800">
                      Firma Başarıyla Oluşturuldu
                    </h3>
                    <p className="text-sm text-emerald-600">
                      Aşağıda oluşturulan firmanın detayları yer almaktadır.
                    </p>
                  </div>
                </div>

                {/* Firma Adı & Tip */}
                <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`p-2.5 rounded-lg border ${getFirmTypeStyle(createdFirm).iconBox}`}>
                      {getFirmIcon(createdFirm, "w-6 h-6")}
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-gray-900">
                        {createdFirm.firmName}
                      </h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${getFirmTypeStyle(createdFirm).chip}`}
                        >
                          {getFirmTypeStyle(createdFirm).label}
                        </span>
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${
                            createdFirm.isCorporate
                              ? "bg-indigo-50 text-indigo-700"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {createdFirm.isCorporate ? "Kurumsal" : "Bireysel"}
                        </span>
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold ${
                            createdFirm.isActive
                              ? "bg-emerald-500/15 text-emerald-700"
                              : "bg-red-500/15 text-red-700"
                          }`}
                        >
                          {createdFirm.isActive ? "Aktif" : "Pasif"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* İletişim */}
                <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    İletişim
                  </h4>
                  <div className="space-y-2.5">
                    {createdFirm.ownerName && (
                      <div className="flex items-center gap-3">
                        <FaUser className="w-4 h-4 text-gray-400 shrink-0" />
                        <span className="text-sm text-gray-800">{createdFirm.ownerName}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-3">
                      <FaEnvelope className="w-4 h-4 text-gray-400 shrink-0" />
                      <span className="text-sm text-gray-800">
                        {createdFirm.email || "—"}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <FaPhone className="w-4 h-4 text-gray-400 shrink-0" />
                      <span className="text-sm text-gray-800">
                        {createdFirm.tel || "—"}
                      </span>
                    </div>
                    {/* Muhataplar (#367) */}
                    {(createdFirm.contacts || []).map((c, i) => (
                      <div key={`c-${i}`} className="flex items-center gap-3">
                        <FaUser className="w-4 h-4 text-gray-400 shrink-0" />
                        <span className="text-sm text-gray-800">
                          {c.name}
                          {c.tel && <span className="text-gray-500"> · {c.tel}</span>}
                        </span>
                      </div>
                    ))}
                    <div className="flex items-start gap-3">
                      <FaMapMarkerAlt className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-800">
                        {createdFirm.address?.text}
                        {createdFirm.address?.town
                          ? `, ${createdFirm.address.town}`
                          : ""}
                        {createdFirm.address?.city
                          ? ` / ${createdFirm.address.city}`
                          : ""}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Ek Bilgiler */}
                <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    Ek Bilgiler
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    {createdFirm.dealerFirm?.firmName && (
                      <div className="col-span-2 flex items-center gap-3">
                        <FaBuilding className="w-4 h-4 text-gray-400 shrink-0" />
                        <span className="text-sm text-gray-600">Bayi:</span>
                        <span className="text-sm font-medium text-gray-800">
                          {createdFirm.dealerFirm.firmName}
                        </span>
                      </div>
                    )}
                    {createdFirm.mainFirmName &&
                      createdFirm.mainFirmName !== "none" && (
                        <div className="col-span-2 flex items-center gap-3">
                          <FaBuilding className="w-4 h-4 text-gray-400 shrink-0" />
                          <span className="text-sm text-gray-600">
                            {createdFirm.firmType === "dealer"
                              ? "Ana Bayi:"
                              : "Ana Firma:"}
                          </span>
                          <span className="text-sm font-medium text-gray-800">
                            {createdFirm.mainFirmName}
                          </span>
                        </div>
                      )}
                    <div className="flex items-center gap-3">
                      <FaCalendarAlt className="w-4 h-4 text-gray-400 shrink-0" />
                      <span className="text-sm text-gray-600">
                        Sipariş Sıklığı:
                      </span>
                      <span className="text-sm font-medium text-gray-800">
                        {createdFirm.orderFrequencyDays} gün
                      </span>
                    </div>
                    {createdFirm.isCorporate && (
                      <>
                        <div className="col-span-2 border-t border-gray-100 pt-2 mt-1">
                          <span className="text-xs font-semibold text-gray-500 uppercase">
                            Vergi Bilgileri
                          </span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-xs text-gray-500">
                            Vergi No
                          </span>
                          <span className="text-sm font-medium text-gray-800">
                            {createdFirm.taxNumber}
                          </span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-xs text-gray-500">
                            Vergi Dairesi
                          </span>
                          <span className="text-sm font-medium text-gray-800">
                            {createdFirm.taxOffice}
                          </span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-xs text-gray-500">
                            Resmi ID
                          </span>
                          <span className="text-sm font-medium text-gray-800">
                            {createdFirm.officialNumber}
                          </span>
                        </div>
                      </>
                    )}
                    {Array.isArray(createdFirm.activeProducts) && (
                      <div className="col-span-2 flex items-start gap-3 mt-1">
                        <FaMugHot className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-600 shrink-0">Aktif Ürünler:</span>
                        <span className="text-sm font-Medium text-gray-800">
                          {createdFirm.activeProducts.join(", ")}
                        </span>
                      </div>
                    )}
                    {createdFirm.note && (
                      <div className="col-span-2 flex items-start gap-3 mt-1">
                        <FaStickyNote className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-800">
                          {createdFirm.note}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : isSubmitting ? (
              <div className="flex flex-col items-center justify-center h-full space-y-4">
                <Loader
                  Visible={true}
                  Width={80}
                  Height={80}
                  Color={"#3B82F6"}
                />
                <p className="text-base font-medium text-gray-700">
                  Firma oluşturuluyor...
                </p>
              </div>
            ) : (
              <>
                {step === 1 && (
                  <FirmInfoStep
                    input={input}
                    errors={errors}
                    onChange={handleInputChange}
                  />
                )}
                {step === 2 && (
                  <FirmContactStep
                    input={input}
                    errors={errors}
                    onChange={handleInputChange}
                    onAddressChange={handleAddressChange}
                  />
                )}
                {step === 3 && (
                  <FirmProductsStep
                    products={products}
                    selected={input.activeProducts}
                    onChange={(next) => handleInputChange("activeProducts", next)}
                    isLoading={productOptions.isLoading}
                    isError={productOptions.isError}
                    error={errors.activeProducts}
                  />
                )}
                {step === 4 && (
                  <FirmSettingsReviewStep
                    input={input}
                    errors={errors}
                    onChange={handleInputChange}
                    onEditStep={(target) => {
                      setErrors({});
                      setStep(target);
                    }}
                    productsAvailable={productsAvailable}
                  />
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex-none border-t bg-gray-50 p-4 sm:p-6">
            <div className="flex justify-end gap-3">
              {createdFirm ? (
                <button
                  onClick={onClose}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <FaTimes className="w-4 h-4" />
                  Kapat
                </button>
              ) : (
                <>
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    İptal
                  </button>

                  {step > 1 && (
                    <button
                      onClick={handleBack}
                      className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <FaArrowLeft className="w-3.5 h-3.5" />
                      Geri
                    </button>
                  )}
                  {step < FIRM_CREATE_STEPS.length ? (
                    <button
                      onClick={handleNext}
                      className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      İleri
                      <FaArrowRight className="w-3.5 h-3.5" />
                    </button>
                  ) : (
                    <button
                      onClick={handleConfirmSubmit}
                      disabled={isSubmitting}
                      className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaCheck className="w-4 h-4" />
                      Onayla ve Oluştur
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* MUI Alert */}
      <Snackbar
        open={alert.open}
        autoHideDuration={2000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alert.type}
          icon={alert.type === "success" ? <CheckCircle /> : <Error />}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CreateFirmDrawer;
